import React from 'react';
import { Box, Typography, LinearProgress, Grid, Chip, IconButton, InputAdornment } from '@mui/material';
import { CheckCircle, Cancel, Visibility, VisibilityOff } from '@mui/icons-material';
import { getPasswordStrengthScore, getPasswordStrengthColor, getPasswordStrengthText } from '../utils/passwordValidation';

const PasswordStrengthIndicator = ({ 
  password, 
  showDetails = true, 
  showPassword = false, 
  onTogglePassword = null,
  showToggleButton = false 
}) => {
  if (!password) {
    return null;
  }

  const strengthData = getPasswordStrengthScore(password);
  const { score, level, feedback, maxScore } = strengthData;
  const progress = (score / maxScore) * 100;
  const color = getPasswordStrengthColor(level);
  const strengthText = getPasswordStrengthText(level);

  // Define password criteria with validation functions
  const passwordCriteria = [
    {
      label: '8+ chars',
      test: (pwd) => pwd.length >= 8,
      description: 'At least 8 characters'
    },
    {
      label: 'A-Z',
      test: (pwd) => /[A-Z]/.test(pwd),
      description: 'Uppercase letter'
    },
    {
      label: 'a-z',
      test: (pwd) => /[a-z]/.test(pwd),
      description: 'Lowercase letter'
    },
    {
      label: '0-9',
      test: (pwd) => /\d/.test(pwd),
      description: 'Number'
    },
    {
      label: '!@#',
      test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
      description: 'Special character'
    },
    {
      label: 'No 3+',
      test: (pwd) => !/(.)\1{2,}/.test(pwd),
      description: 'No repeated chars'
    },
    {
      label: 'No seq',
      test: (pwd) => {
        const commonSequences = ['123', 'abc', 'qwe', 'asd', 'zxc'];
        const passwordLower = pwd.toLowerCase();
        return !commonSequences.some(seq => passwordLower.includes(seq));
      },
      description: 'No common sequences'
    }
  ];

  // Check which criteria are met
  const criteriaStatus = passwordCriteria.map(criteria => ({
    ...criteria,
    met: criteria.test(password)
  }));

  const metCount = criteriaStatus.filter(c => c.met).length;
  const totalCriteria = criteriaStatus.length;

  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      {/* Password Strength Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Password Strength:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 'bold',
              color: color
            }}
          >
            {strengthText}
          </Typography>
          {showToggleButton && onTogglePassword && (
            <IconButton
              size="small"
              onClick={onTogglePassword}
              sx={{ 
                p: 0.5,
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>
      
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: '#e0e0e0',
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 3,
          }
        }}
      />

      {/* Progress Text */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        {metCount} of {totalCriteria} criteria met
      </Typography>
      
      {/* Compact Criteria Chips */}
      {showDetails && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'medium' }}>
            Requirements:
          </Typography>
          <Grid container spacing={0.5} sx={{ flexWrap: 'wrap' }}>
            {criteriaStatus.map((criteria, index) => (
              <Grid item key={index}>
                <Chip
                  icon={criteria.met ? 
                    <CheckCircle fontSize="small" /> : 
                    <Cancel fontSize="small" />
                  }
                  label={criteria.label}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.7rem',
                    height: '24px',
                    borderColor: criteria.met ? 'success.main' : 'error.main',
                    color: criteria.met ? 'success.main' : 'error.main',
                    '& .MuiChip-icon': {
                      color: criteria.met ? 'success.main' : 'error.main',
                      fontSize: '0.9rem'
                    },
                    '& .MuiChip-label': {
                      px: 1,
                      fontSize: '0.7rem'
                    }
                  }}
                  title={criteria.description}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Additional Feedback */}
      {feedback.length > 0 && (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="caption" color="info.dark" sx={{ display: 'block', mb: 0.5, fontWeight: 'medium' }}>
            💡 Suggestions:
          </Typography>
          {feedback.slice(0, 2).map((item, index) => (
            <Typography 
              key={index} 
              variant="caption" 
              color="info.dark"
              sx={{ 
                display: 'block',
                fontSize: '0.75rem',
                lineHeight: 1.2
              }}
            >
              • {item}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PasswordStrengthIndicator; 